import * as propTypes from 'prop-types';
import * as React from 'react';
import styled from '@emotion/styled';
import { getCurrencyString } from '../../../components/PriceString';
import * as colors from '../../../consts/variables';
import { replacePlaceholders, __ } from '../../../helpers/TranslationService';
import { ICruiseReducer, IRate } from '../../../interfaces';
import loadable from '@loadable/component';
import { CalendarIconNew } from '../../../components/Icons/CalendarIconNew';

const CustomSelect = loadable(() => import('../../../components/CustomSelect/CustomSelect'));

interface IProps {
    onChange: (index: number) => void;
    options: { id: number; label: string; nid: number; maxDiscount?: number }[];
    selectedId: number;
    rates: IRate[];
    ratesBySail?: ICruiseReducer['ratesBySail'];
    currency: string;
}

interface IState {
    selectTextIndentBy: number;
    selectWidth: string;
}

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        backgroundColor: colors.transparent,
        position: 'relative',
        width: '100%',
        fontSize: 14,
        padding: 16,
        paddingLeft: 12,
        color: colors.softBlack,
        gap: 6,
    },
    oneSailWrapper: {
        borderRadius: 6,
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        backgroundColor: colors.white,
        border: `2px solid ${colors.grey_e6}`,
        fontSize: 14,
        padding: 16,
        paddingLeft: 12,
        color: colors.softBlack,
        gap: 6,
    },
    disabledWrapper: {
        borderRadius: 6,
        height: 55,
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        backgroundColor: colors.grey_f8,
        border: `2px solid ${colors.grey_e6}`,
        fontSize: 14,
        padding: '0 15px',
        color: colors.softBlack,
    },
    select: {
        opacity: 1,
        height: '100%',
        backgroundColor: colors.transparent,
        border: 'none',
        color: colors.white,
    },
    wrapperRef: {
        display: 'flex',
        alignItems: 'center',
    },
    option: {
        color: colors.black,
        padding: '10px',
    },
    discountWrapper: {
        fontSize: 11,
        color: colors.pink,
        fontWeight: 'bold',
        lineHeight: 1.83,
    },
    arrowStyle: { marginLeft: 'auto' },
};

const CalendarIconStyled = styled(CalendarIconNew)`
    fill: ${({ theme }) => theme.icon.brandColor1};
    stroke: ${({ theme }) => theme.icon.brandColor1};
    flex: 0 0 auto;
    display: block;
    height: 16px;
    width: 16px;
`;

function ValueComponent(props) {
    return (
        <div style={styles.wrapper}>
            <CalendarIconStyled width={20} height={20} />
            {props.value.label}
        </div>
    );
}

interface IValue {
    value: string;
    label?: string;
    icon?: string;
    maxDiscount?: number;
}

interface IOptionProp {
    option: IValue;
    onSelect(IValue): void;
}

class OptionComponent extends React.Component<IOptionProp> {
    handleClick = () => this.props.onSelect(this.props.option);

    render() {
        return (
            <div onClick={this.handleClick} style={styles.option}>
                <span>{this.props.option.label}</span>
                {this.props.option.maxDiscount && (
                    <div style={styles.discountWrapper}>
                        {replacePlaceholders(__('upToXDiscount', 'dreamlines'), [
                            `-${this.props.option.maxDiscount}%`,
                        ])}
                    </div>
                )}
            </div>
        );
    }
}

function getPerPersonPriceString(price: number | undefined, currency: string) {
    const priceWithCurrencySymbol = getCurrencyString(price, currency, false);
    return typeof price === 'number'
        ? __('from {{ displayPrice }} p.P.', 'dreamlines').replace(
              '{{ displayPrice }}',
              priceWithCurrencySymbol,
          )
        : __('request.onRequest', 'dreamlines');
}

export class SailSelect extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);

        this.state = {
            selectTextIndentBy: -100,
            selectWidth: 'auto',
        };
    }

    static contextTypes = {
        track: propTypes.func,
    };

    wrapperRef = null;
    selectRef = null;
    getWrapperRef = (ref) => (this.wrapperRef = ref);
    getSelectRef = (ref) => (this.selectRef = ref);

    calculateTextIndentation = () => {
        const extraSpace = this.wrapperRef.clientWidth - parseInt(this.state.selectWidth, 10);
        this.setState({
            selectTextIndentBy: extraSpace > 0 ? extraSpace / 2 : 0,
        });
    };

    handleChange = ({ id, label }) => {
        const value = parseInt(id, 10);
        this.props.onChange(value);
        if (this.context.track) {
            this.context.track({ action: 'date-selection', label });
        }
    };

    render() {
        const isSingleSail = this.props.options.length === 1;
        const { selectedId, options, ratesBySail, currency } = this.props;
        const calendarWrapperStyle: React.CSSProperties = !isSingleSail
            ? {
                  position: 'absolute',
                  left: `${this.state.selectTextIndentBy - 25}px`,
              }
            : {};
        const selectOptions = options.map((op) => {
            const sailRate = ratesBySail[op.nid];
            return {
                value: `${op.label} (${getPerPersonPriceString(sailRate?.minPrice, currency)})`,
                label: `${op.label} (${getPerPersonPriceString(sailRate?.minPrice, currency)})`,
                id: op.id,
                maxDiscount: sailRate?.maxDiscount ? sailRate?.maxDiscount : null,
            };
        });
        const value = {
            value: selectedId === -1 ? options[0]?.label : options?.[selectedId]?.label,
            label: selectedId === -1 ? options[0]?.label : options?.[selectedId]?.label,
            id: selectedId,
        };
        const Sails = isSingleSail ? (
            <div ref={this.getWrapperRef} style={styles.oneSailWrapper}>
                <div style={calendarWrapperStyle}>
                    <CalendarIconStyled width={20} height={20} />
                </div>
                <div ref={this.getSelectRef} style={styles.wrapperRef}>
                    {this.props.options[0].label}
                </div>
            </div>
        ) : (
            <CustomSelect
                ref={this.getSelectRef}
                value={value}
                valueComponent={ValueComponent}
                options={selectOptions}
                optionComponent={OptionComponent}
                onChange={this.handleChange}
                clearable={false}
                searchable={false}
                style={{
                    width: '100%',
                    height: '100%',
                }}
            />
        );

        return <div id="sail-select">{Sails}</div>;
    }
}
